import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useResetUserPassword } from '@/services/users';

interface ResetPasswordButtonProps {
  userId: string;
}

/**
 * Ação exclusiva da listagem de usuários: gera uma senha temporária pro usuário e força a
 * troca no próximo login dele (`needToChangePassword`). A senha só aparece uma vez, no
 * diálogo de resultado — depois de fechado não tem como recuperar (precisa resetar de novo).
 */
export function ResetPasswordButton({ userId }: ResetPasswordButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { mutate: resetPassword, isPending } = useResetUserPassword();
  const toast = useToast();

  const handleConfirm = () => {
    resetPassword(userId, {
      onSuccess: (data) => {
        setConfirmOpen(false);
        setTempPassword(data.password);
      },
    });
  };

  const handleCopy = async () => {
    if (!tempPassword) return;
    await navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    toast.success('Senha copiada!');
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setConfirmOpen(true)}
          >
            <Icon name="key-round" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Resetar senha</TooltipContent>
      </Tooltip>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resetar senha</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Isso gera uma senha temporária e força a troca dela no próximo login desse
            usuário. Quer continuar?
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isPending}
              onClick={handleConfirm}
            >
              Resetar senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!tempPassword}
        onOpenChange={(open) => !open && setTempPassword(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Senha temporária gerada</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Copie e envie essa senha pro usuário — ela só aparece aqui uma vez. Ele vai
            precisar trocá-la no próximo login.
          </p>
          <div className="flex items-center gap-2">
            <Input readOnly value={tempPassword ?? ''} className="font-mono" />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={copied ? 'text-green-500' : undefined}
              onClick={handleCopy}
            >
              <Icon name={copied ? 'check-check' : 'copy'} />
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setTempPassword(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
